'use client'

import {createContext, useContext, useEffect, useRef, ReactNode} from 'react'

interface MessageEventCallbacks {
  [eventType: string]: ((data?: any) => void)[]
}

export enum GameEvent {
  Loaded = 'loaded',
  Playing = 'playing',
  LevelUp = 'levelup',
  Over = 'over',
  Loading = 'loading',
  Paused = 'paused',
  Start = 'start',
  Login = 'login-user',
  Logout = 'logout-user',
  IsLoggedIn = 'is-user-logged-in',
}

interface MessageContextType {
  registerGameEvent: (eventType: GameEvent, callback: (data?: any) => void) => void
  unregisterGameEvent: (eventType: GameEvent, callback: (data?: any) => void) => void
  registerAdEvent: (adType: string, callback: (data?: any) => void) => void
  unregisterAdEvent: (adType: string, callback: (data?: any) => void) => void
}

const GameEventBridgeContext = createContext<MessageContextType | undefined>(undefined)

export const GameEventBridgeProvider = ({children}: {children: ReactNode}) => {
  const gameCallbacks = useRef<MessageEventCallbacks>({})
  const adCallbacks = useRef<MessageEventCallbacks>({})
  const lastGameEvent = useRef<string>('')
  const hasStartedLoading = useRef<boolean>(false)

  useEffect(() => {
    const handleMessageEvent = (event: MessageEvent) => {
      const eventData = event.data
      const {state, type, typeAd} = eventData

      if (type === GameEvent.Loading && !hasStartedLoading.current) {
        hasStartedLoading.current = true
        gameCallbacks.current[type]?.forEach((callback) => callback(eventData))
      }

      if (state && lastGameEvent.current !== state) {
        lastGameEvent.current = state
        gameCallbacks.current[state]?.forEach((callback) => callback(eventData))
      }

      if (typeAd) {
        adCallbacks.current[typeAd]?.forEach((callback) => callback(eventData))
      }
    }

    window.addEventListener('message', handleMessageEvent)
    return () => {
      window.removeEventListener('message', handleMessageEvent)
    }
  }, [])

  const registerGameEvent = (eventType: GameEvent, callback: (data?: any) => void) => {
    if (!gameCallbacks.current[eventType]) {
      gameCallbacks.current[eventType] = []
    }
    gameCallbacks.current[eventType].push(callback)
  }

  const unregisterGameEvent = (eventType: GameEvent, callback: (data?: any) => void) => {
    if (gameCallbacks.current[eventType]) {
      gameCallbacks.current[eventType] = gameCallbacks.current[eventType].filter((cb) => cb !== callback)
    }
  }

  const registerAdEvent = (adType: string, callback: (data?: any) => void) => {
    if (!adCallbacks.current[adType]) {
      adCallbacks.current[adType] = []
    }
    adCallbacks.current[adType].push(callback)
  }

  const unregisterAdEvent = (adType: string, callback: (data?: any) => void) => {
    if (adCallbacks.current[adType]) {
      adCallbacks.current[adType] = adCallbacks.current[adType].filter((cb) => cb !== callback)
    }
  }

  return (
    <GameEventBridgeContext.Provider
      value={{
        registerGameEvent,
        unregisterGameEvent,
        registerAdEvent,
        unregisterAdEvent,
      }}
    >
      {children}
    </GameEventBridgeContext.Provider>
  )
}

export const useMessageEvents = () => {
  const context = useContext(GameEventBridgeContext)
  if (!context) {
    throw new Error('useMessageEvents must be used within a MessageProvider')
  }
  return context
}
